/*
 * Popular add-ons inside the cart drawer.
 * Each tap is one POST to /cart/add.js, built the same way as product-form.js
 * (FormData, sections, no Content-Type). It never calls /cart/update.js or
 * /cart/change.js. The dormant safety net (ccl-cart-safety-net.js) already
 * watches this request and re-adds only if the variant is missing from /cart.js.
 */
if (!customElements.get('ccl-cart-addons')) {
  customElements.define(
    'ccl-cart-addons',
    class CclCartAddons extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;
        this.dataset.bound = 'true';
        this.pending = false;
        this.addEventListener('click', this.onClick.bind(this));

        const list = this.querySelector('.ccl-cart-addons__list');
        if (this.dataset.seedProduct && list && !list.children.length) {
          this.loadRecommendations();
        }
      }

      onClick(event) {
        const button = event.target.closest('[data-variant-id]');
        if (!button || button.tagName === 'A') return;
        event.preventDefault();
        this.addVariant(button);
      }

      async loadRecommendations() {
        const seed = this.dataset.seedProduct;
        if (!seed) return;

        try {
          const response = await fetch(
            `/recommendations/products.json?product_id=${encodeURIComponent(seed)}&limit=10&intent=related`
          );
          if (!response.ok) return;
          const data = await response.json();
          const exclude = new Set(
            (this.dataset.excludeIds || '')
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean)
          );
          const products = (data.products || []).filter((product) => this.isRecommendation(product, exclude)).slice(0, 2);
          if (!products.length) return;

          const list = this.querySelector('.ccl-cart-addons__list');
          products.forEach((product) => {
            const card = this.cardFor(product);
            if (card) list.appendChild(card);
          });
          if (list.children.length) this.hidden = false;
        } catch {
          /* Recommendations are optional. A failed read must not affect the cart. */
        }
      }

      isRecommendation(product, exclude) {
        const type = String(product.type || '').toLowerCase();
        if (type === 'shipwill' || type === 'gift card' || type === 'giftcard') return false;
        const protectHandle = (this.dataset.protectHandle || 'shipping-protection').toLowerCase();
        if (String(product.handle || '').toLowerCase() === protectHandle) return false;
        if (exclude.has(String(product.id))) return false;
        if (product.available === false) return false;
        const variants = product.variants || [];
        if (variants.length && !variants.some((variant) => variant.available)) return false;
        return true;
      }

      cardFor(product) {
        const variants = (product.variants || []).filter((variant) => variant.available);
        const item = document.createElement('li');
        item.className = 'ccl-cart-addon';

        const media = document.createElement('a');
        media.className = 'ccl-cart-addon__media';
        media.href = product.url || '#';
        media.tabIndex = -1;
        media.setAttribute('aria-label', product.title || 'Product');
        const src = this.imageSrc(product);
        if (src) {
          const image = document.createElement('img');
          image.src = src;
          image.alt = '';
          image.width = 240;
          image.height = 240;
          image.loading = 'lazy';
          media.appendChild(image);
        }

        const title = document.createElement('a');
        title.className = 'ccl-cart-addon__title';
        title.href = product.url || '#';
        title.textContent = product.title || '';

        const price = document.createElement('span');
        price.className = 'ccl-cart-addon__price';
        price.textContent = this.formatMoney(product.price);

        item.append(media, title, price);

        if (variants.length === 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ccl-cart-addon__add';
          button.dataset.variantId = String(variants[0].id);
          button.textContent = this.dataset.addLabel || 'Add';
          const hidden = document.createElement('span');
          hidden.className = 'visually-hidden';
          hidden.textContent = ` ${product.title || ''}`;
          button.appendChild(hidden);
          item.appendChild(button);
        } else if (variants.length > 1) {
          const link = document.createElement('a');
          link.className = 'ccl-cart-addon__add';
          link.href = product.url || '#';
          link.textContent = this.dataset.chooseLabel || 'Choose';
          const hidden = document.createElement('span');
          hidden.className = 'visually-hidden';
          hidden.textContent = ` ${product.title || ''}`;
          link.appendChild(hidden);
          item.appendChild(link);
        } else {
          return null;
        }

        return item;
      }

      imageSrc(product) {
        const image = product.featured_image;
        let src = '';
        if (!image) return '';
        if (typeof image === 'string') src = image;
        else src = image.src || image.url || '';
        if (src.startsWith('//')) src = `https:${src}`;
        if (!src) return '';
        const sized = src.includes('?') ? `${src}&width=240` : `${src}?width=240`;
        return sized;
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = Number(cents) || 0;
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const formatWith = (precision, thousands, decimal) => {
          const fixed = (value / 100).toFixed(precision);
          const parts = fixed.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
          return parts[1] ? `${dollars}${decimal}${parts[1]}` : dollars;
        };
        const amount =
          {
            amount: formatWith(2, ',', '.'),
            amount_no_decimals: formatWith(0, ',', '.'),
            amount_with_comma_separator: formatWith(2, '.', ','),
            amount_no_decimals_with_comma_separator: formatWith(0, '.', ','),
            amount_with_apostrophe_separator: formatWith(2, "'", '.'),
          }[(format.match(placeholder) || [])[1]] || formatWith(2, ',', '.');
        return format.replace(placeholder, amount);
      }

      async addVariant(button) {
        if (this.pending || button.getAttribute('aria-disabled') === 'true') return;
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        this.pending = true;
        button.setAttribute('aria-disabled', 'true');
        this.setError('');

        const cart = document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        try {
          const config =
            typeof fetchConfig === 'function'
              ? fetchConfig('javascript')
              : { method: 'POST', headers: { Accept: 'application/javascript' } };
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          const response = await fetch(routes.cart_add_url, config);
          const data = await response.json();
          if (data.status) {
            this.setError(data.description || data.message || this.dataset.errorLabel);
            this.pending = false;
            button.removeAttribute('aria-disabled');
            return;
          }

          if (cart && data.sections && typeof cart.renderContents === 'function') {
            cart.classList.remove('is-empty');
            try {
              cart.renderContents(data);
            } catch (renderError) {
              console.error(renderError);
              await this.refreshDrawer(cart);
            }
            return;
          }

          if (cart) {
            await this.refreshDrawer(cart);
            return;
          }

          window.location = window.routes.cart_url;
        } catch (error) {
          console.error(error);
          this.setError(this.dataset.errorLabel);
          this.pending = false;
          button.removeAttribute('aria-disabled');
        }
      }

      async refreshDrawer(cart) {
        const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
        const html = await response.text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
        const current = document.querySelector('#CartDrawer');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        cart.classList.remove('is-empty');
        const overlay = cart.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', cart.close.bind(cart));
        cart.open();
      }

      setError(message) {
        const error = this.querySelector('.ccl-cart-addons__error');
        if (!error) return;
        error.textContent = message || '';
        error.hidden = !message;
      }
    }
  );
}
